import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Input,
  PageHeader,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  useToast,
} from '@biartbt/biartd'
import type { FormMasterItem } from '../api/formMasterApi'
import { formMasterApi, PARENT_MENU_OPTIONS } from '../api/formMasterApi'

type SortDir = 'asc' | 'desc'

export default function FormMasterPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<string | undefined>(undefined)
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const [data, setData] = useState<{ items: FormMasterItem[]; totalCount: number } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<any>(null)

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<FormMasterItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Debounced search
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput)
      setPage(1)
    }, 350)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const fetchList = useCallback(
    async (opts?: { silent?: boolean }) => {
      setError(null)
      setIsFetching(true)
      if (!opts?.silent) setIsLoading(true)
      try {
        const res = await formMasterApi.list({
          search: search.trim() || undefined,
          page,
          pageSize,
          sortBy: (sortBy as 'entityName' | 'displayName' | 'parentMenu' | 'updatedAt') ?? undefined,
          sortDir,
        })
        setData(res)
      } catch (e) {
        setError(e)
      } finally {
        setIsFetching(false)
        setIsLoading(false)
      }
    },
    [search, page, pageSize, sortBy, sortDir],
  )

  useEffect(() => {
    void fetchList()
  }, [fetchList])

  const items = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const parentMenuLabel = useCallback(
    (value: string) => {
      const opt = PARENT_MENU_OPTIONS.find((o) => o.value === value)
      return opt ? t(opt.label) : value
    },
    [t],
  )

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(key)
      setSortDir('asc')
    }
  }

  const columns = useMemo(
    () => [
      { key: 'entityName', header: t('formMaster.columns.entityName'), sortable: true, widthClassName: 'w-[160px]' },
      { key: 'displayName', header: t('formMaster.columns.displayName'), sortable: true, widthClassName: 'w-[200px]' },
      { key: 'parentMenu', header: t('formMaster.columns.parentMenu'), sortable: true, widthClassName: 'w-[140px]' },
      { key: 'menuName', header: t('formMaster.columns.menuName'), sortable: false, widthClassName: 'w-[160px]' },
      { key: 'backendRoute', header: t('formMaster.columns.backendRoute'), sortable: false, widthClassName: 'w-[180px]' },
      { key: 'updatedAt', header: t('formMaster.columns.updatedAt'), sortable: true, widthClassName: 'w-[160px]' },
    ],
    [t],
  )

  const renderCell = (item: FormMasterItem, key: string) => {
    switch (key) {
      case 'entityName':
        return <span className="font-medium">{item.entityName}</span>
      case 'displayName':
        return item.displayName
      case 'parentMenu':
        return <Badge variant="secondary">{parentMenuLabel(item.parentMenu)}</Badge>
      case 'menuName':
        return item.menuName
      case 'backendRoute':
        return <span className="font-mono text-xs">{item.backendRoute}</span>
      case 'updatedAt':
        return item.updatedAt
          ? new Date(item.updatedAt).toLocaleString('tr-TR')
          : item.createdAt
            ? new Date(item.createdAt).toLocaleString('tr-TR')
            : '—'
      default:
        return null
    }
  }

  const requestDelete = (item: FormMasterItem) => {
    setDeleteTarget(item)
    setConfirmDeleteOpen(true)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await formMasterApi.delete(deleteTarget.id)
      toast({ title: t('formMaster.toast.deleted'), description: t('formMaster.toast.deletedDesc'), variant: 'success' })
      await fetchList({ silent: true })
    } catch (e: any) {
      toast({ title: t('formMaster.toast.error'), description: e?.message ?? t('formMaster.toast.deleteFailed'), variant: 'danger' })
    } finally {
      setIsDeleting(false)
      setConfirmDeleteOpen(false)
      setDeleteTarget(null)
    }
  }

  return (
    <div className="p-[25px]">
      <PageHeader
        title={t('formMaster.title')}
        subtitle={t('formMaster.subtitle')}
        actions={
          <>
            <Button variant="outline" onClick={() => fetchList()} disabled={isFetching || isLoading}>
              {t('formMaster.actions.refresh')}
            </Button>
            <Button
              leftIcon={<i className="material-symbols-outlined">add</i>}
              onClick={() => navigate('/system/form-master/new')}
              disabled={isLoading}
            >
              {t('formMaster.newDefinition')}
            </Button>
          </>
        }
      />

      {error && (
        <div className="mt-4">
          <Alert variant="danger" type="default">
            {error?.message ?? t('formMaster.toast.loadFailed')}
          </Alert>
        </div>
      )}

      <Card className="mt-4">
        <TableToolbar
          className="mb-4"
          left={
            <div className="min-w-[260px]">
              <Input
                aria-label="Search"
                placeholder={t('formMaster.searchPlaceholder')}
                value={searchInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    setSearch(searchInput)
                    setPage(1)
                  }
                }}
                leftIcon={<i className="material-symbols-outlined text-lg">search</i>}
              />
            </div>
          }
          right={
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('formMaster.total')}: <span className="font-medium text-gray-900 dark:text-gray-100">{totalCount}</span>
            </div>
          }
        />

        {isLoading ? (
          <div className="grid gap-3">
            <Skeleton heightClassName="h-8" />
            <Skeleton heightClassName="h-8" />
            <Skeleton heightClassName="h-8" />
            <Skeleton heightClassName="h-8" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={t('formMaster.emptyState.title')}
            description={t('formMaster.emptyState.description')}
            action={<Button onClick={() => navigate('/system/form-master/new')}>{t('formMaster.newDefinition')}</Button>}
            icon={<i className="material-symbols-outlined text-3xl">inventory_2</i>}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((c) => (
                    <TableHead key={c.key} className={c.widthClassName}>
                      {c.sortable ? (
                        <Button variant="ghost" size="sm" onClick={() => toggleSort(c.key)}>
                          {c.header} {sortBy === c.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                        </Button>
                      ) : (
                        c.header
                      )}
                    </TableHead>
                  ))}
                  <TableHead className="text-right">{t('formMaster.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/system/form-master/${item.id}/edit`)}
                  >
                    {columns.map((c) => (
                      <TableCell key={c.key} className={c.widthClassName}>
                        {renderCell(item, c.key)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/system/form-master/${item.id}/edit`)
                          }}
                        >
                          {t('formMaster.actions.edit')}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            requestDelete(item)
                          }}
                        >
                          {t('formMaster.actions.delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex items-center justify-end mt-[12px]">
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} disabled={isFetching} />
            </div>
          </>
        )}
      </Card>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t('formMaster.deleteConfirm.title')}
        description={t('formMaster.deleteConfirm.description')}
        confirmLabel={t('formMaster.actions.delete')}
        cancelLabel={t('formMaster.actions.cancel')}
        confirmVariant="destructive"
        loading={isDeleting}
        onConfirm={confirmDelete}
      />
    </div>
  )
}
